import { getInstallerDashboardApplications } from '../services/salesforce';
import useAuth from './useAuth';
import { EXTERNAL_ACTION_ORDER, EXTERNAL_ACTIONS } from '../constants';
import { Contact, DashboardApplication } from '../../../../types/api';
import { QueryClient, useQuery } from 'react-query';

export const APPLICATIONS_QUERY_KEY = 'applications';

type ApplicationsQueryData = {
  active: DashboardApplication[];
  changeOrder: DashboardApplication[];
  approved: DashboardApplication[];
  closed: DashboardApplication[];
  statuses: string[];
};

// Patch nickname on the app with this applicationId; leave other rows unchanged.
function withUpdatedProjectNickname(
  apps: DashboardApplication[],
  applicationId: string,
  projectNickname: string | null
): DashboardApplication[] {
  return apps.map((app) =>
    String(app.id) === String(applicationId)
      ? { ...app, project_nickname: projectNickname }
      : app
  );
}

/** Keep installer dashboard surfaces in sync after a nickname PATCH. */
export function updateProjectNicknameInApplicationsCache(
  queryClient: QueryClient,
  applicationId: string,
  projectNickname: string | null
): void {
  queryClient.setQueriesData<ApplicationsQueryData | undefined>(
    [APPLICATIONS_QUERY_KEY],
    (old) => {
      if (!old) {
        return undefined;
      }
      return {
        ...old,
        active: withUpdatedProjectNickname(
          old.active,
          applicationId,
          projectNickname
        ),
        changeOrder: withUpdatedProjectNickname(
          old.changeOrder,
          applicationId,
          projectNickname
        ),
        approved: withUpdatedProjectNickname(
          old.approved,
          applicationId,
          projectNickname
        ),
        closed: withUpdatedProjectNickname(
          old.closed,
          applicationId,
          projectNickname
        ),
      };
    }
  );
}

const fetchApplications = async (user?: Contact | null) => {
  try {
    const response = await getInstallerDashboardApplications(
      user?.account?.id ?? '',
      user?.account?.type ?? '',
      user?.installer_role ?? '',
      user?.id ?? ''
    );

    const active: any[] = [];
    const changeOrder: any[] = [];
    const approved: any[] = [];
    const closed: any[] = [];
    const statuses: string[] = [];

    for (const app of response) {
      switch (app.external_status_action) {
        case EXTERNAL_ACTIONS.PENDING_CHANGE_REQUEST:
          changeOrder.push(app);
          break;
        case EXTERNAL_ACTIONS.APP_CREATED_PENDING_CR_EXTERNAL:
          changeOrder.push(app);
          break;
        case EXTERNAL_ACTIONS.APPLICATION_PENDING_CHANGE_REQUEST_EXTERNAL:
          changeOrder.push(app);
          break;
        case EXTERNAL_ACTIONS.LOAN_APPROVED:
          approved.push(app);
          break;
        case EXTERNAL_ACTIONS.CLOSED:
          closed.push(app);
          break;
        default:
          active.push(app);
          if (!statuses.includes(app.external_status_action)) {
            statuses.push(app.external_status_action);
          }
          break;
      }
    }

    statuses.sort((a, b) => {
      const numberA = EXTERNAL_ACTION_ORDER.get(a) ?? 1000;
      const numberB = EXTERNAL_ACTION_ORDER.get(b) ?? 1000;
      return numberA - numberB;
    });

    return {
      active,
      changeOrder,
      approved,
      closed,
      statuses,
    };
  } catch (error) {
    throw new Error(
      error instanceof Error ? error.message : 'An unknown error occurred'
    );
  }
};

export function useApplications() {
  const { user } = useAuth();
  const query = useQuery(
    [
      APPLICATIONS_QUERY_KEY,
      user?.account?.id,
      user?.account?.type,
      user?.installer_role,
      user?.id,
    ],
    () => fetchApplications(user),
    {
      enabled:
        !!user?.account?.id &&
        !!user?.account?.type &&
        !!user?.installer_role &&
        !!user?.id,
    }
  );
  return {
    ...query,
    data: query.data ?? {
      active: [],
      changeOrder: [],
      approved: [],
      closed: [],
      statuses: [],
    },
    totalApplications:
      (query.data?.active?.length ?? 0) +
      (query.data?.closed?.length ?? 0) +
      (query.data?.approved?.length ?? 0) +
      (query.data?.changeOrder?.length ?? 0),
  };
}
